import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import FleetMapPanel from './components/FleetMapPanel';
import BookingCreationPanel from './components/BookingCreationPanel';
import ActiveOrdersList from './components/ActiveOrdersList';
import DriverAvailabilityPanel from './components/DriverAvailabilityPanel';
import ToastNotification from './components/ToastNotification';
import { orderService, driverService, realtimeService } from '../../services/supabaseService';

// Mock data fallback for when Supabase data is unavailable
const MOCK_DRIVERS = [
  { id: '1', name: 'James Mwangi', vehicleType: 'Sedan', licensePlate: 'KCA 123A', status: 'idle', rating: 4.8, totalTrips: 312, currentLocation: 'CBD', zone: 'CBD', lastActivity: new Date(Date.now() - 5 * 60000).toISOString() },
  { id: '2', name: 'Sarah Wanjiku', vehicleType: 'SUV', licensePlate: 'KBZ 456B', status: 'active', rating: 4.9, totalTrips: 521, currentLocation: 'Westlands', zone: 'Westlands', lastActivity: new Date(Date.now() - 2 * 60000).toISOString() },
  { id: '3', name: 'Peter Kamau', vehicleType: 'Van', licensePlate: 'KCC 789C', status: 'en_route', rating: 4.6, totalTrips: 198, currentLocation: 'Eastlands', zone: 'Eastlands', lastActivity: new Date(Date.now() - 1 * 60000).toISOString() },
  { id: '4', name: 'Grace Njeri', vehicleType: 'Motorcycle', licensePlate: 'KMCD 01', status: 'idle', rating: 4.7, totalTrips: 445, currentLocation: 'Southlands', zone: 'Southlands', lastActivity: new Date(Date.now() - 8 * 60000).toISOString() },
  { id: '5', name: 'David Ochieng', vehicleType: 'Sedan', licensePlate: 'KDA 321D', status: 'offline', rating: 4.5, totalTrips: 267, currentLocation: 'CBD', zone: 'CBD', lastActivity: new Date(Date.now() - 45 * 60000).toISOString() },
];

const MOCK_ORDERS = [
  { id: '1', orderNumber: 'OTG-2026-1001', serviceType: 'ride', status: 'pending', pickupAddress: 'Kenyatta Avenue, CBD', dropoffAddress: 'Westlands Mall, Westlands', customer: { fullName: 'Alice Kamau' }, driver: null, totalAmount: 450, createdAt: new Date(Date.now() - 10 * 60000).toISOString(), isUrgent: false },
  { id: '2', orderNumber: 'OTG-2026-1002', serviceType: 'delivery', status: 'driver_assigned', pickupAddress: 'Industrial Area Warehouse', dropoffAddress: 'Karen Shopping Centre', customer: { fullName: 'Bob Omondi' }, driver: { name: 'James Mwangi', licensePlate: 'KCA 123A' }, totalAmount: 850, createdAt: new Date(Date.now() - 25 * 60000).toISOString(), isUrgent: true },
  { id: '3', orderNumber: 'OTG-2026-1003', serviceType: 'ride', status: 'picked_up', pickupAddress: 'JKIA Terminal 1', dropoffAddress: 'Hilton Hotel, CBD', customer: { fullName: 'Carol Wambui' }, driver: { name: 'Sarah Wanjiku', licensePlate: 'KBZ 456B' }, totalAmount: 1200, createdAt: new Date(Date.now() - 40 * 60000).toISOString(), isUrgent: false },
  { id: '4', orderNumber: 'OTG-2026-1004', serviceType: 'delivery', status: 'pending', pickupAddress: 'Gikomba Market', dropoffAddress: 'Ngong Road, Kilimani', customer: { fullName: 'Dennis Mutua' }, driver: null, totalAmount: 600, createdAt: new Date(Date.now() - 5 * 60000).toISOString(), isUrgent: false },
];

const DispatcherConsoleScreen = () => {
  const [drivers, setDrivers] = useState(MOCK_DRIVERS);
  const [orders, setOrders] = useState(MOCK_ORDERS);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('map'); // mobile tab: map | orders | drivers | booking
  const [filters, setFilters] = useState({ status: 'all', serviceType: 'all' });
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const addNotification = useCallback((title, message, type = 'info') => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, title, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev?.filter(n => n?.id !== id));
    }, 5000);
  }, []);

  const dismissNotification = (id) => {
    setNotifications(prev => prev?.filter(n => n?.id !== id));
  };

  // Load data from Supabase
  const loadData = useCallback(async () => {
    try {
      const [driversData, ordersData] = await Promise.all([
        driverService?.getAllDrivers(),
        orderService?.getAllOrders({ limit: 50 }),
      ]);
      if (driversData?.length > 0) setDrivers(driversData);
      if (ordersData?.length > 0) setOrders(ordersData);
      setLastRefresh(new Date());
    } catch (err) {
      console.warn('Using mock data — Supabase unavailable:', err?.message);
    }
  }, []);

  useEffect(() => {
    loadData();

    // Real-time subscriptions
    const newOrdersSub = realtimeService?.subscribeToNewOrders((payload) => {
      const newOrder = payload?.new;
      if (newOrder) {
        setOrders(prev => [orderService?._mapOrder?.(newOrder) || newOrder, ...prev]);
        addNotification('New Order', `Order ${newOrder?.order_number} received`, 'info');
      }
    });

    const statusSub = realtimeService?.subscribeToOrderStatusChanges((payload) => {
      const updated = payload?.new;
      if (updated) {
        setOrders(prev => prev?.map(o => o?.id === updated?.id ? { ...o, status: updated?.status } : o));
        addNotification('Order Updated', `Order ${updated?.order_number} → ${updated?.status}`, 'success');
      }
    });

    const driverSub = realtimeService?.subscribeToDriverStatusChanges((payload) => {
      const updated = payload?.new;
      if (updated) {
        setDrivers(prev => prev?.map(d => d?.id === updated?.id ? { ...d, status: updated?.status } : d));
      }
    });

    // Auto-refresh every 30s
    const refreshTimer = setInterval(loadData, 30000);

    return () => {
      realtimeService?.unsubscribe(newOrdersSub);
      realtimeService?.unsubscribe(statusSub);
      realtimeService?.unsubscribe(driverSub);
      clearInterval(refreshTimer);
    };
  }, [loadData, addNotification]);

  // Initial page skeleton timer
  useEffect(() => {
    const timer = setTimeout(() => setPageLoading(false), 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleBookingCreate = async (formData) => {
    setLoading(true);
    try {
      const mockCustomerId = 'mock-customer-id';
      const orderData = {
        serviceType: formData?.serviceType,
        vehicleType: formData?.vehicleType,
        pickupAddress: formData?.pickupAddress,
        dropoffAddress: formData?.dropoffAddress,
        notes: formData?.notes,
        fareAmount: Math.floor(Math.random() * 800 + 300),
        platformFee: 50,
        totalAmount: Math.floor(Math.random() * 800 + 350),
      };

      try {
        const created = await orderService?.createOrder(mockCustomerId, orderData);
        setOrders(prev => [{ ...created, isUrgent: formData?.isUrgent, customer: { fullName: formData?.customerName } }, ...prev]);
      } catch {
        // Fallback: add mock order
        const mockOrder = {
          id: Date.now()?.toString(),
          orderNumber: 'OTG-2026-' + Math.floor(Math.random() * 9000 + 1000),
          serviceType: formData?.serviceType,
          status: 'pending',
          pickupAddress: formData?.pickupAddress,
          dropoffAddress: formData?.dropoffAddress,
          customer: { fullName: formData?.customerName || 'Walk-in Customer' },
          driver: null,
          totalAmount: orderData?.totalAmount,
          createdAt: new Date()?.toISOString(),
          isUrgent: formData?.isUrgent,
        };
        setOrders(prev => [mockOrder, ...prev]);
      }

      addNotification(
        formData?.isUrgent ? '🚨 Urgent Order Created' : 'Order Created',
        `${formData?.serviceType} booking dispatched from ${formData?.pickupAddress}`,
        formData?.isUrgent ? 'urgent' : 'success'
      );
    } catch (err) {
      console.error('Booking error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleFlagUrgent = async (orderId) => {
    setOrders(prev => prev?.map(o => o?.id === orderId ? { ...o, isUrgent: true } : o));
    addNotification('🚨 Urgent Flag Set', 'Order escalated to priority status', 'urgent');
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await orderService?.updateOrderStatus(orderId, newStatus);
    } catch {
      // Fallback: update locally
    }
    setOrders(prev => prev?.map(o => o?.id === orderId ? { ...o, status: newStatus } : o));
    addNotification('Status Updated', `Order moved to ${newStatus?.replace('_', ' ')}`, 'success');
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const filteredOrders = orders?.filter(o => {
    const statusMatch = filters?.status === 'all' || o?.status === filters?.status;
    const typeMatch = filters?.serviceType === 'all' || o?.serviceType === filters?.serviceType;
    return statusMatch && typeMatch;
  });

  const stats = {
    totalActive: orders?.filter(o => !['delivered', 'cancelled']?.includes(o?.status))?.length,
    pending: orders?.filter(o => o?.status === 'pending')?.length,
    onlineDrivers: drivers?.filter(d => d?.status !== 'offline')?.length,
    urgent: orders?.filter(o => o?.isUrgent && !['delivered', 'cancelled']?.includes(o?.status))?.length,
  };

  const mobileTabItems = [
    { id: 'map', icon: 'Map', label: 'Fleet' },
    { id: 'orders', icon: 'List', label: 'Orders' },
    { id: 'drivers', icon: 'Users', label: 'Drivers' },
    { id: 'booking', icon: 'Plus', label: 'Book' },
  ];

  // Staggered skeleton for Dispatcher Console
  if (pageLoading) {
    return (
      <div className="min-h-screen bg-[#0a1628] flex flex-col">
        {/* Skeleton Header */}
        <header className="bg-[#0a1628] border-b border-white/10 px-4 lg:px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-lg bg-[#ffd700]/30 animate-pulse" style={{ animationDelay: '0ms' }} />
              <div className="w-px h-6 bg-white/20" />
              <div className="space-y-1.5">
                <div className="h-3.5 w-36 rounded-full bg-white/20 animate-pulse" style={{ animationDelay: '60ms' }} />
                <div className="h-2.5 w-44 rounded-full bg-white/10 animate-pulse hidden sm:block" style={{ animationDelay: '120ms' }} />
              </div>
            </div>
            {/* Center stats skeleton */}
            <div className="hidden md:flex items-center space-x-1">
              {[180, 240, 300, 360].map(delay => (
                <div key={delay} className="px-3 py-1.5 bg-white/10 rounded-lg min-w-16 space-y-1 animate-pulse" style={{ animationDelay: `${delay}ms` }}>
                  <div className="h-5 w-6 rounded bg-white/20 animate-pulse mx-auto" style={{ animationDelay: `${delay + 20}ms` }} />
                  <div className="h-2 w-10 rounded-full bg-white/10 animate-pulse mx-auto" style={{ animationDelay: `${delay + 40}ms` }} />
                </div>
              ))}
            </div>
            {/* Right actions skeleton */}
            <div className="flex items-center space-x-2">
              <div className="w-20 h-7 rounded-lg bg-white/10 animate-pulse" style={{ animationDelay: '420ms' }} />
              <div className="w-14 h-7 rounded-lg bg-green-500/20 animate-pulse" style={{ animationDelay: '480ms' }} />
              <div className="w-16 h-7 rounded-lg bg-[#ffd700]/20 animate-pulse" style={{ animationDelay: '540ms' }} />
            </div>
          </div>
        </header>

        {/* Desktop Skeleton Layout */}
        <div className="hidden lg:flex flex-1 overflow-hidden p-4 gap-4">
          {/* Left Column: Booking Panel + Driver Availability */}
          <div className="w-72 xl:w-80 flex flex-col gap-4 flex-shrink-0">
            {/* Booking Creation Panel Skeleton */}
            <div className="bg-[#0f2040] rounded-xl border border-white/10 p-4 space-y-3 flex-1 max-h-96">
              <div className="h-4 w-36 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '200ms' }} />
              <div className="space-y-2.5">
                {[250, 310, 370, 430].map(delay => (
                  <div key={delay} className="space-y-1">
                    <div className="h-2.5 w-20 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                    <div className="h-9 rounded-lg bg-white/8 animate-pulse" style={{ animationDelay: `${delay + 30}ms` }} />
                  </div>
                ))}
              </div>
              <div className="h-10 rounded-lg bg-[#ffd700]/20 animate-pulse mt-2" style={{ animationDelay: '500ms' }} />
            </div>

            {/* Driver Availability Panel Skeleton */}
            <div className="bg-[#0f2040] rounded-xl border border-white/10 p-4 space-y-3 flex-1">
              <div className="flex items-center justify-between">
                <div className="h-4 w-32 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '300ms' }} />
                <div className="h-5 w-10 rounded-full bg-green-500/20 animate-pulse" style={{ animationDelay: '360ms' }} />
              </div>
              <div className="space-y-2">
                {[350, 420, 490, 560].map(delay => (
                  <div key={delay} className="flex items-center gap-2.5 p-2 rounded-lg bg-white/5 animate-pulse" style={{ animationDelay: `${delay}ms` }}>
                    <div className="w-8 h-8 rounded-full bg-white/10 animate-pulse flex-shrink-0" style={{ animationDelay: `${delay + 20}ms` }} />
                    <div className="flex-1 space-y-1">
                      <div className="h-2.5 w-24 rounded-full bg-white/12 animate-pulse" style={{ animationDelay: `${delay + 40}ms` }} />
                      <div className="h-2 w-16 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: `${delay + 60}ms` }} />
                    </div>
                    <div className="w-12 h-5 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: `${delay + 80}ms` }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Center: Fleet Map Skeleton */}
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex-1 bg-[#0f2040] rounded-xl border border-white/10 overflow-hidden relative animate-pulse" style={{ animationDelay: '250ms' }}>
              {/* Map grid */}
              <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid slice">
                {[0, 80, 160, 240, 320, 400, 480, 560, 600].map(y => (
                  <line key={`h${y}`} x1="0" y1={y} x2="800" y2={y} stroke="#ffd700" strokeWidth="0.5" />
                ))}
                {[0, 100, 200, 300, 400, 500, 600, 700, 800].map(x => (
                  <line key={`v${x}`} x1={x} y1="0" x2={x} y2="600" stroke="#ffd700" strokeWidth="0.5" />
                ))}
              </svg>
              {/* Driver pin skeletons */}
              {[
                { top: '30%', left: '25%', delay: 400 },
                { top: '50%', left: '55%', delay: 500 },
                { top: '65%', left: '35%', delay: 600 },
                { top: '25%', left: '70%', delay: 700 },
              ].map((pin, i) => (
                <div
                  key={i}
                  className="absolute w-8 h-8 rounded-full bg-white/15 animate-pulse border border-white/20"
                  style={{ top: pin.top, left: pin.left, animationDelay: `${pin.delay}ms` }}
                />
              ))}
              {/* Map label */}
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <div className="h-3 w-20 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '350ms' }} />
                <div className="w-2 h-2 rounded-full bg-green-400/50 animate-pulse" />
              </div>
            </div>
          </div>

          {/* Right Column: Active Orders Skeleton */}
          <div className="w-80 xl:w-96 flex-shrink-0">
            <div className="h-full bg-[#0f2040] rounded-xl border border-white/10 p-4 space-y-3 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between pb-2 border-b border-white/10">
                <div className="h-4 w-28 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '300ms' }} />
                <div className="h-5 w-8 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '360ms' }} />
              </div>
              {/* Filter pills */}
              <div className="flex gap-2">
                {[400, 460, 520].map(delay => (
                  <div key={delay} className="h-6 w-16 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: `${delay}ms` }} />
                ))}
              </div>
              {/* Order rows */}
              <div className="space-y-2.5">
                {[450, 530, 610, 690, 770].map(delay => (
                  <div key={delay} className="bg-white/5 rounded-xl p-3 space-y-2 animate-pulse" style={{ animationDelay: `${delay}ms` }}>
                    <div className="flex items-center justify-between">
                      <div className="h-3 w-28 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: `${delay + 20}ms` }} />
                      <div className="h-5 w-16 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: `${delay + 40}ms` }} />
                    </div>
                    <div className="space-y-1">
                      <div className="h-2.5 w-full rounded-full bg-white/8 animate-pulse" style={{ animationDelay: `${delay + 60}ms` }} />
                      <div className="h-2.5 w-4/5 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: `${delay + 80}ms` }} />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <div className="h-6 flex-1 rounded-lg bg-white/8 animate-pulse" style={{ animationDelay: `${delay + 100}ms` }} />
                      <div className="h-6 flex-1 rounded-lg bg-[#ffd700]/15 animate-pulse" style={{ animationDelay: `${delay + 120}ms` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Skeleton Layout */}
        <div className="flex lg:hidden flex-1 flex-col overflow-hidden">
          <div className="flex-1 p-3">
            {/* Map skeleton for mobile */}
            <div className="h-full min-h-64 bg-[#0f2040] rounded-xl border border-white/10 relative animate-pulse" style={{ animationDelay: '300ms' }}>
              <svg className="absolute inset-0 w-full h-full opacity-8" viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice">
                {[0, 60, 120, 180, 240, 300].map(y => (
                  <line key={`h${y}`} x1="0" y1={y} x2="400" y2={y} stroke="#ffd700" strokeWidth="0.5" />
                ))}
                {[0, 80, 160, 240, 320, 400].map(x => (
                  <line key={`v${x}`} x1={x} y1="0" x2={x} y2="300" stroke="#ffd700" strokeWidth="0.5" />
                ))}
              </svg>
              {[
                { top: '35%', left: '30%', delay: 450 },
                { top: '55%', left: '60%', delay: 550 },
              ].map((pin, i) => (
                <div key={i} className="absolute w-7 h-7 rounded-full bg-white/15 animate-pulse border border-white/20" style={{ top: pin.top, left: pin.left, animationDelay: `${pin.delay}ms` }} />
              ))}
            </div>
          </div>
          {/* Mobile Tab Bar Skeleton */}
          <div className="flex-shrink-0 bg-[#0a1628] border-t border-white/10">
            <div className="grid grid-cols-4">
              {[0, 1, 2, 3].map(i => (
                <div key={i} className="flex flex-col items-center justify-center py-3 space-y-1">
                  <div className="w-5 h-5 rounded bg-white/10 animate-pulse" style={{ animationDelay: `${600 + i * 60}ms` }} />
                  <div className="h-2 w-10 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: `${630 + i * 60}ms` }} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Status Bar Skeleton */}
        <div className="hidden lg:flex flex-shrink-0 bg-[#060e1c] border-t border-white/10 px-6 py-1.5 items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="h-2.5 w-36 rounded-full bg-white/10 animate-pulse" style={{ animationDelay: '700ms' }} />
            <div className="h-2.5 w-28 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: '760ms' }} />
          </div>
          <div className="flex items-center space-x-3">
            <div className="h-2.5 w-24 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: '820ms' }} />
            <div className="h-2.5 w-24 rounded-full bg-white/8 animate-pulse" style={{ animationDelay: '880ms' }} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Toast Notifications */}
      <ToastNotification notifications={notifications} onDismiss={dismissNotification} />

      {/* Top Header */}
      <header className="bg-primary border-b border-primary-800 px-4 lg:px-6 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          {/* Left: Brand + Title */}
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <Icon name="Navigation" size={18} color="#1a365d" />
              </div>
              <span className="text-white font-bold text-lg hidden sm:block">OnTheGo</span>
            </Link>
            <div className="w-px h-6 bg-white/20" />
            <div>
              <h1 className="text-white font-semibold text-sm lg:text-base">Dispatcher Console</h1>
              <p className="text-white/50 text-xs hidden sm:block">Operations Command Center</p>
            </div>
          </div>

          {/* Center: Stats */}
          <div className="hidden md:flex items-center space-x-1">
            {[
              { label: 'Active', value: stats?.totalActive, color: 'text-accent' },
              { label: 'Pending', value: stats?.pending, color: 'text-warning' },
              { label: 'Drivers', value: stats?.onlineDrivers, color: 'text-success' },
              { label: 'Urgent', value: stats?.urgent, color: 'text-error' },
            ]?.map(stat => (
              <div key={stat?.label} className="px-3 py-1.5 bg-white/10 rounded-lg text-center min-w-16">
                <p className={`text-lg font-bold ${stat?.color}`}>{stat?.value}</p>
                <p className="text-white/60 text-xs">{stat?.label}</p>
              </div>
            ))}
          </div>

          {/* Right: Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={loadData}
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-colors duration-200"
            >
              <Icon name="RefreshCw" size={13} />
              <span className="hidden sm:block">Refresh</span>
            </button>
            <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-success/20 rounded-lg">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span className="text-success text-xs font-medium">Live</span>
            </div>
            <Link
              to="/admin-dashboard"
              className="flex items-center space-x-1.5 px-3 py-1.5 bg-accent text-primary text-xs font-semibold rounded-lg hover:bg-accent-400 transition-colors duration-200"
            >
              <Icon name="LayoutDashboard" size={13} />
              <span className="hidden sm:block">Admin</span>
            </Link>
          </div>
        </div>

        {/* Mobile Stats Row */}
        <div className="flex md:hidden items-center space-x-2 mt-2 overflow-x-auto pb-1">
          {[
            { label: 'Active', value: stats?.totalActive, color: 'text-accent' },
            { label: 'Pending', value: stats?.pending, color: 'text-warning' },
            { label: 'Drivers', value: stats?.onlineDrivers, color: 'text-success' },
            { label: 'Urgent', value: stats?.urgent, color: 'text-error' },
          ]?.map(stat => (
            <div key={stat?.label} className="px-3 py-1 bg-white/10 rounded-lg text-center flex-shrink-0">
              <span className={`text-sm font-bold ${stat?.color}`}>{stat?.value}</span>
              <span className="text-white/60 text-xs ml-1">{stat?.label}</span>
            </div>
          ))}
        </div>
      </header>

      {/* Desktop Layout */}
      <div className="hidden lg:flex flex-1 overflow-hidden p-4 gap-4">
        {/* Left Column: Booking + Driver Availability */}
        <div className="w-72 xl:w-80 flex flex-col gap-4 flex-shrink-0">
          <div className="flex-1 min-h-0 max-h-96 xl:max-h-none">
            <BookingCreationPanel drivers={drivers} onBookingCreate={handleBookingCreate} loading={loading} />
          </div>
          <div className="flex-1 min-h-0">
            <DriverAvailabilityPanel
              drivers={drivers}
              onDriverSelect={setSelectedDriver}
              selectedDriver={selectedDriver}
            />
          </div>
        </div>

        {/* Center: Fleet Map */}
        <div className="flex-1 min-w-0 flex flex-col">
          <FleetMapPanel
            drivers={drivers}
            onDriverSelect={setSelectedDriver}
            selectedDriver={selectedDriver}
          />
        </div>

        {/* Right Column: Active Orders */}
        <div className="w-80 xl:w-96 flex-shrink-0">
          <ActiveOrdersList
            orders={filteredOrders}
            onFlagUrgent={handleFlagUrgent}
            onUpdateStatus={handleUpdateStatus}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>
      </div>

      {/* Tablet Layout (md to lg) */}
      <div className="hidden md:flex lg:hidden flex-1 overflow-hidden p-3 gap-3 flex-col">
        {/* Top row: Map + Orders */}
        <div className="flex gap-3 flex-1 min-h-0">
          <div className="flex-1 min-w-0">
            <FleetMapPanel
              drivers={drivers}
              onDriverSelect={setSelectedDriver}
              selectedDriver={selectedDriver}
            />
          </div>
          <div className="w-72 flex-shrink-0">
            <ActiveOrdersList
              orders={filteredOrders}
              onFlagUrgent={handleFlagUrgent}
              onUpdateStatus={handleUpdateStatus}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          </div>
        </div>
        {/* Bottom row: Booking + Drivers */}
        <div className="flex gap-3 h-72 flex-shrink-0">
          <div className="flex-1">
            <BookingCreationPanel drivers={drivers} onBookingCreate={handleBookingCreate} loading={loading} />
          </div>
          <div className="flex-1">
            <DriverAvailabilityPanel
              drivers={drivers}
              onDriverSelect={setSelectedDriver}
              selectedDriver={selectedDriver}
            />
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="flex md:hidden flex-1 flex-col overflow-hidden">
        {/* Tab Content */}
        <div className="flex-1 overflow-hidden p-3">
          {activeTab === 'map' && (
            <FleetMapPanel
              drivers={drivers}
              onDriverSelect={setSelectedDriver}
              selectedDriver={selectedDriver}
            />
          )}
          {activeTab === 'orders' && (
            <ActiveOrdersList
              orders={filteredOrders}
              onFlagUrgent={handleFlagUrgent}
              onUpdateStatus={handleUpdateStatus}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
          )}
          {activeTab === 'drivers' && (
            <DriverAvailabilityPanel
              drivers={drivers}
              onDriverSelect={setSelectedDriver}
              selectedDriver={selectedDriver}
            />
          )}
          {activeTab === 'booking' && (
            <BookingCreationPanel drivers={drivers} onBookingCreate={handleBookingCreate} loading={loading} />
          )}
        </div>

        {/* Mobile Tab Bar */}
        <div className="flex-shrink-0 bg-primary border-t border-primary-800">
          <div className="grid grid-cols-4">
            {mobileTabItems?.map(tab => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex flex-col items-center justify-center py-3 space-y-1 transition-colors duration-200 ${
                  activeTab === tab?.id
                    ? 'text-accent border-t-2 border-accent' :'text-white/50 hover:text-white/80'
                }`}
              >
                <Icon name={tab?.icon} size={18} />
                <span className="text-xs font-medium">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer Status Bar */}
      <div className="hidden lg:flex flex-shrink-0 bg-primary-900 border-t border-primary-800 px-6 py-1.5 items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1.5">
            <div className="w-1.5 h-1.5 bg-success rounded-full animate-pulse" />
            <span className="text-white/50 text-xs">Supabase Connected</span>
          </div>
          <span className="text-white/30 text-xs">
            Last sync: {lastRefresh?.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
        </div>
        <div className="flex items-center space-x-4 text-white/40 text-xs">
          <span>{drivers?.filter(d => d?.status !== 'offline')?.length} drivers online</span>
          <span>•</span>
          <span>{orders?.filter(o => !['delivered', 'cancelled']?.includes(o?.status))?.length} active orders</span>
          <span>•</span>
          <span>OnTheGo Dispatcher v1.0</span>
        </div>
      </div>
    </div>
  );
};

export default DispatcherConsoleScreen;
